import clsx from 'clsx';
import GithubSlugger from 'github-slugger';
import { useEffect, useMemo, useState } from 'react';
import styles from './Docs.module.scss';

export type TocEntry = {
  id: string;
  title: string;
  depth: 2 | 3;
};

/**
 * Pulls the `##` and `###` headings out of a markdown source.
 *
 * `#` is the page title and would just repeat the heading already on screen;
 * anything below `###` makes the list longer than the content it indexes.
 *
 * Ids come from the same slugger `rehype-slug` uses to stamp them onto the
 * rendered headings, so the two agree without either knowing about the other.
 * Fenced code blocks are skipped — a `# comment` inside one is not a heading.
 */
export function extractToc(markdown: string): TocEntry[] {
  const slugger = new GithubSlugger();
  const entries: TocEntry[] = [];
  let inFence = false;

  for (const line of markdown.split('\n')) {
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = /^(#{2,3})\s+(.*)$/.exec(line);
    if (!match) continue;

    const title = match[2].replace(/\s+#*\s*$/, '').trim();

    entries.push({
      id: slugger.slug(title),
      title,
      depth: match[1].length as 2 | 3,
    });
  }

  return entries;
}

/**
 * Highlights whichever heading is currently at the top of the reading area.
 *
 * Uses a top-biased root margin so a heading counts as "current" once it
 * reaches the upper part of the viewport, rather than only while it is
 * centred — which is what makes the highlight track reading rather than lag
 * behind it.
 */
function useActiveHeading(entries: TocEntry[]): string | undefined {
  const [activeId, setActiveId] = useState<string | undefined>();

  useEffect(() => {
    if (entries.length === 0) return undefined;

    const observer = new IntersectionObserver(
      observed => {
        const visible = observed
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    const headings = entries
      .map(entry => document.getElementById(entry.id))
      .filter((element): element is HTMLElement => !!element);

    headings.forEach(heading => observer.observe(heading));

    return () => observer.disconnect();
  }, [entries]);

  return activeId;
}

type DocsTocProps = {
  markdown: string;
};

export function DocsToc({ markdown }: DocsTocProps) {
  const entries = useMemo(() => extractToc(markdown), [markdown]);
  const activeId = useActiveHeading(entries);

  if (entries.length === 0) {
    return null;
  }

  return (
    <nav className={styles.toc} aria-label="On this page">
      <p className={styles.tocHeading}>On this page</p>
      {entries.map(entry => (
        <a
          key={entry.id}
          href={`#${entry.id}`}
          className={clsx(
            styles.tocLink,
            entry.depth === 3 && styles.tocLinkNested,
            entry.id === activeId && styles.tocLinkActive
          )}
        >
          {entry.title}
        </a>
      ))}
    </nav>
  );
}

export default DocsToc;
